/**
 * One-time import of per-origin browser storage from the app's previous host
 * (react.<env>.westernbulk.cloud). Chrome partitions third-party iframe
 * storage, so the hand-over is a top-level round trip: the first visit here
 * bounces to the legacy host's `migrate.html`, which sends its non-auth
 * localStorage back in the URL fragment; this side imports what is missing.
 * Auth state is per origin by design and is never copied. A nonce issued
 * before the bounce and echoed back ties the import to a round trip this app
 * started, so a crafted link cannot seed storage. Gives up for good after a
 * few failed attempts so a dead legacy host cannot trap the user.
 */
export const MIGRATED_KEY = "wb.migrated-from-legacy";
const ATTEMPTS_KEY = "wb.migrated-from-legacy.attempts";
const NONCE_KEY = "wb.migrated-from-legacy.nonce";
const MAX_ATTEMPTS = 3;
export const IMPORT_FRAGMENT = "wb-import";
export const NONCE_PARAM = "nonce";
const NEVER_IMPORTED = ["oidc.", "wb.auth.", "wb.permissions.cache", "wb.migrated-from-legacy"];

export type ExportedStorage = Record<string, string>;

/** Entries worth importing: not auth state, and not already set on this origin. */
export function keysToImport(
  exported: ExportedStorage,
  existingKeys: readonly string[]
): ExportedStorage {
  const existing = new Set(existingKeys);
  return Object.fromEntries(
    Object.entries(exported).filter(
      ([key, value]) =>
        typeof value === "string" &&
        !existing.has(key) &&
        !NEVER_IMPORTED.some((p) => key.startsWith(p))
    )
  );
}

export interface ImportFragment {
  entries: ExportedStorage;
  nonce: string;
}

/** The payload carried back in `#wb-import=<json>&nonce=<n>`: empty entries
 *  when unreadable, `null` when the fragment is not an import at all. */
export function readImportFragment(hash: string): ImportFragment | null {
  const params = new URLSearchParams(hash.replace(/^#/, ""));
  const raw = params.get(IMPORT_FRAGMENT);
  if (raw === null) return null;
  const nonce = params.get(NONCE_PARAM) ?? "";
  try {
    const parsed: unknown = JSON.parse(raw);
    const entries = parsed !== null && typeof parsed === "object" ? (parsed as ExportedStorage) : {};
    return { entries, nonce };
  } catch {
    return { entries: {}, nonce };
  }
}

/** Whether this load should bounce to the legacy host at all. */
export function shouldMigrate(
  storage: Pick<Storage, "getItem">,
  currentOrigin: string,
  legacyOrigin: string
): boolean {
  if (!legacyOrigin || currentOrigin === legacyOrigin) return false;
  if (storage.getItem(MIGRATED_KEY)) return false;
  return Number(storage.getItem(ATTEMPTS_KEY) ?? "0") < MAX_ATTEMPTS;
}

export function migrateUrl(legacyOrigin: string, returnTo: string, nonce: string): string {
  const query = new URLSearchParams({ return: returnTo, [NONCE_PARAM]: nonce });
  return `${legacyOrigin}/migrate.html?${query}`;
}

function storageKeys(storage: Storage): string[] {
  return Array.from({ length: storage.length }, (_, i) => storage.key(i) ?? "");
}

export function migrateLegacyStorage(legacyOrigin: string): void {
  const storage = window.localStorage;
  const imported = readImportFragment(window.location.hash);
  if (imported !== null) {
    window.history.replaceState(
      window.history.state,
      "",
      window.location.pathname + window.location.search
    );
    const expected = window.sessionStorage.getItem(NONCE_KEY);
    window.sessionStorage.removeItem(NONCE_KEY);
    if (!expected || imported.nonce !== expected || storage.getItem(MIGRATED_KEY)) return;
    const entries = keysToImport(imported.entries, storageKeys(storage));
    Object.entries(entries).forEach(([key, value]) => storage.setItem(key, value));
    storage.setItem(MIGRATED_KEY, new Date().toISOString());
    storage.removeItem(ATTEMPTS_KEY);
    return;
  }
  if (!shouldMigrate(storage, window.location.origin, legacyOrigin)) return;
  storage.setItem(ATTEMPTS_KEY, String(Number(storage.getItem(ATTEMPTS_KEY) ?? "0") + 1));
  const nonce = crypto.randomUUID();
  window.sessionStorage.setItem(NONCE_KEY, nonce);
  window.location.replace(migrateUrl(legacyOrigin, window.location.href, nonce));
}
